/**
 * imageUtils.js — upgrade common image-CDN URLs to higher-resolution variants.
 *
 * BBC, Sky, ESPN, Goal, Guardian, and Unsplash all encode the requested
 * resolution in the URL path or query-string. Many RSS feeds emit a tiny
 * thumbnail (160-240 px wide) that becomes blurry on a 1200 px hero. This
 * helper rewrites the URL to request a 1200 px (or arbitrary) variant.
 */

/**
 * Upgrade an image URL to a higher resolution.
 * @param {string} url - source URL
 * @param {number} width - desired width in pixels (default 1200)
 * @returns {string} upgraded URL — falls back to the original if no rule matched
 */
export const upgradeImageUrl = (url, width = 1200) => {
    if (!url || typeof url !== "string") return url;
    let out = url;

    try {
        // Unsplash: ?w=200&q=60 → ?w=1200&q=80
        if (out.includes("images.unsplash.com")) {
            out = out.replace(/([?&])w=\d+/g, `$1w=${width}`);
            out = out.replace(/([?&])q=\d+/g, `$1q=85`);
            if (!/[?&]w=/.test(out)) {
                out += (out.includes("?") ? "&" : "?") + `w=${width}&q=85`;
            }
            return out;
        }

        // BBC ichef: /ace/standard/240/cpsprodpb/...  → /ace/standard/1200/cpsprodpb/...
        if (out.includes("ichef.bbci.co.uk")) {
            return out.replace(/\/(?:ace\/(?:standard|alternate)|news\/\d+)\/(\d+)\//, (m, sz) => {
                return m.replace(`/${sz}/`, `/1200/`);
            });
        }

        // Sky News / Sky Sports: ?bypass-service-worker&w=200 → ...&w=1200
        if (out.includes("sky.com") || out.includes("skysports.com")) {
            return out.replace(/([?&])w=\d+/g, `$1w=${width}`);
        }

        // ESPN CDN: /r/id/.../h/100  → /r/id/.../h/600
        if (out.includes("a.espncdn.com")) {
            return out
                .replace(/\/h\/\d+/, `/h/600`)
                .replace(/\/w\/\d+/, `/w/${width}`);
        }

        // Guardian CDN: /img/media/.../master/0_0_3712_2475.jpg?width=300
        if (out.includes("guim.co.uk")) {
            return out.replace(/([?&])width=\d+/g, `$1width=${width}`);
        }

        // CNN CDN
        if (out.includes("cdn.cnn.com")) {
            return out.replace(/-w\d+/, `-w${width}`)
                       .replace(/-h\d+/, "");
        }

        // Goal.com CDN
        if (out.includes("goal.com") && /\/\d+x\d+\//.test(out)) {
            return out.replace(/\/\d+x\d+\//, `/${width}x0/`);
        }
    } catch (_) {
        return url;
    }
    return out;
};

/** Default Unsplash hero fallback — high-res sport image. */
export const FALLBACK_HERO = "https://images.unsplash.com/photo-1551958219-acbc608c6377?w=1600&q=85";
export const FALLBACK_THUMB = "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=800&q=85";
